import { createContext, useContext, useState, useEffect, ReactNode } from "react";

export type Language = "en" | "ta";

const translations = {
  en: {
    // Navigation
    home: "Home",
    trips: "Trips",
    earnings: "Earnings",
    profile: "Profile",
    report: "Report",
    vehicles: "Vehicles",
    drivers: "Drivers",
    dashboard: "Dashboard",

    // Profile menu
    my_profile: "My Profile",
    notifications: "Notifications",
    theme: "Theme",
    language: "Language",
    settings: "Settings",
    sign_out: "Sign Out",
    dark_mode: "Dark mode",
    light_mode: "Light mode",
    manage_alerts: "Manage your alerts",
    app_preferences: "App preferences & support",
    log_out: "Log out of your account",
    english: "English",
    tamil: "Tamil",
    select_language: "Select your language",

    // Driver info
    driver_information: "Driver Information",
    driver_app: "Driver App",
    phone: "Phone",
    driver_id: "Driver ID",
    vehicle_reg: "Vehicle Reg. No.",
    vehicle_id: "Vehicle ID",
    online: "Online",
    offline: "Offline",

    // Trip status
    head_to_pickup: "Head to pickup",
    customer_waiting: "Customer waiting — Start trip",
    trip_in_progress: "Trip in Progress",

    // Actions
    accept: "Accept",
    decline: "Decline",
    reached: "I've Reached",
    start_trip: "Start Trip",
    end_trip: "End Trip",
    cancel_trip: "Cancel Trip",
    cancel: "Cancel",
    confirm: "Confirm",
    submit: "Submit",
    skip: "Skip",
    call: "Call",

    // Trip page
    trip_history: "Trip History",
    all: "All",
    completed: "Completed",
    cancelled: "Cancelled",
    no_trips: "No trips found",
    complete_first_trip: "Complete your first trip to see it here",
    loading_trips: "Loading trips…",

    // Earnings page
    net_earnings: "Net Earnings",
    total_collection: "Total Collection",
    today: "Today",
    week: "Week",
    month: "Month",
    this_week: "This Week",
    yesterday: "Yesterday",
    last_7_days: "Last 7 Days",
    custom_range: "Custom",
    penalties: "Penalties",
    gross_earnings: "Gross earnings",
    net: "Net",
    commission: "commission",
    transaction_history: "Transaction History",
    no_transactions: "No transactions for",
    loading_earnings: "Loading earnings…",
    trip_earning: "Trip",
    bonus: "Bonus",
    penalty: "Penalty",
    deduction: "Deduction",

    // Performance page
    performance_report: "Performance Report",
    performance: "Performance",
    ratings: "Ratings",
    total: "Total",
    total_bookings: "Total Bookings",
    completed_trips: "Completed Trips",
    cancelled_trips: "Cancelled Trips",
    worked_hours: "Worked Hours",
    total_hours: "Total Hours",
    online_hours: "Online Hours",
    on_trip_hours: "On-Trip Hours",
    commission_rate: "Commission Rate",
    rating_breakdown: "Rating Breakdown",
    excellent: "Excellent",
    good: "Good",
    average: "Average",
    below_avg: "Below Avg",
    poor: "Poor",
    based_on: "Based on",
    ratings_label: "ratings",
    loading_report: "Loading report…",

    // Notifications
    no_notifications: "No notifications yet",
    notifications_empty_sub: "You'll see trip alerts and updates here",
    loading_notifications: "Loading notifications…",

    // Trip completion
    trip_completed: "Trip Completed!",
    final_fare: "Final Fare",
    cash_collect: "Cash • Collect from customer",
    received_cash: "Received Cash",
    rate_customer: "Rate Customer",
    submit_rating: "Submit Rating",
    distance: "Distance",
    duration: "Duration",
    payment: "Payment",

    // OTP & Opening KM
    enter_otp: "Enter OTP",
    enter_otp_sub: "Ask your customer for the 4-digit OTP",
    verify_otp: "Verify & Start Trip",
    enter_opening_km: "Enter Opening KM",
    opening_km_sub: "Record your odometer reading before starting",
    odometer_reading: "Odometer reading (km)",
    save_continue: "Save & Continue",

    // Cancel trip
    cancel_reason: "Cancel Reason",
    select_reason: "Select reason",
    cancel_confirm: "Are you sure you want to cancel?",

    // Availability
    go_online: "Go Online",
    go_offline: "Go Offline",
    today_trips: "Today's Trips",
    today_earnings: "Today's Earnings",

    // Vendor profile
    basic_information: "Basic Information",
    document_verification: "Document Verification",
    bank_details: "Bank Details",
    contact_person: "Contact Person",
    payment_mode: "Payment Mode",
    address: "Address",
    email: "Email",
    additional_phone: "Additional Phone",
    add_email: "Add Email",
    date_of_birth: "Date of Birth",
    vendor_id_label: "Vendor ID",
    verified: "Verified",
    pending: "Pending",
    aadhaar: "Aadhaar",
    pan: "PAN",
    gst: "GST",
    vendor_app: "Vendor App",

    // Vendor dashboard
    welcome: "Welcome",
    active_vehicles: "Active Vehicles",
    active_drivers: "Active Drivers",
    todays_trips: "Today's Trips",
    todays_revenue: "Today's Revenue",
    alerts: "Alerts",

    // Vendor trips
    fleet_trips: "Fleet Trips",
    no_trips_yet: "No trips yet",

    // Vendor earnings
    earnings_ledger: "Earnings & Ledger",
    credits: "Credits",
    debits: "Debits",
    balance: "Balance",
    recent_transactions: "Recent Transactions",
    no_transactions_yet: "No transactions yet",

    // Vendor vehicles
    my_vehicles: "My Vehicles",
    no_vehicles: "No vehicles assigned",
    insurance: "Insurance",
    fitness: "Fitness",
    permit: "Permit",

    // Vendor drivers
    my_drivers: "My Drivers",
    no_drivers: "No drivers assigned",

    // Vendor notifications
    updates_here: "You'll see updates and alerts here",

    // Common
    loading: "Loading…",
    no_data: "No data",
    safe_track: "Safe Track",
    driver_app_version: "Driver App v1.0.0",
    transaction: "transaction",
    transactions: "transactions",
    hrs: "hrs",
    km: "km",
    min: "min",
    cash: "Cash",
    vehicle_breakdown: "Vehicle Breakdown",
    personal_emergency: "Personal Emergency",
    customer_unreachable: "Customer Unreachable",
    other: "Other",
    not_logged_in: "You are not logged in",

    // Vendor dashboard extras
    welcome_back: "Welcome back,",
    fleet_today_sub: "Here's what's happening with your fleet today.",
    quick_actions: "Quick Actions",
    fleet_management: "Fleet Management",
    fleet_tracking: "Fleet Tracking",
    add_vehicle: "Add Vehicle",
    add_driver: "Add Driver",
    doc_expiry_overview: "Document Expiry Overview",
    vehicle_performance: "Vehicle Performance",
    driver_performance: "Driver Performance",
    couldnt_load_dashboard: "Couldn't load dashboard stats. Tap to retry.",
    no_vehicles_yet: "No vehicles yet.",
    no_drivers_yet: "No drivers yet.",

    // Exit dialog
    exit_app: "Exit App",
    exit_app_confirm: "Are you sure you want to exit?",
    yes: "Yes",
    no: "No",

    // Notifications page
    mark_all_read: "Mark all read",
    read: "Read",
    unread: "Unread",

    // Settings
    push_notifications: "Push Notifications",
    manage_alert_prefs: "Manage alert preferences",
    delete_account: "Delete Account",
    permanently_remove: "Permanently remove your account",
    settings_sub: "Notifications, theme, language",

    // Document status
    valid: "Valid",
    expiring: "Expiring",
    expired: "Expired",
    missing: "Missing",
    ins: "Ins",
    fit: "Fit",
    pmt: "Pmt",

    // Period filters
    this_month: "This Month",
    no_trips_period: "No trips in this period",
    balance_label: "Balance",

    // Tabs / status
    active_tab: "Active",
    inactive_tab: "Inactive",
    unverified: "Unverified",
    no_active_vehicles: "No active vehicles",
    no_inactive_vehicles: "No inactive vehicles",
    no_unverified_vehicles: "No unverified vehicles",
    no_active_drivers: "No active drivers",
    no_inactive_drivers: "No inactive drivers",
    no_unverified_drivers: "No unverified drivers",
    docs_required: "Docs Required",
    suspended: "Suspended",
    under_review: "Under Review",
    add: "Add",

    // Profile menu subtitles
    manage_vehicles: "Manage vehicles",
    manage_drivers: "Manage drivers",
    view_vehicle_perf: "View vehicle performance",
    view_driver_perf: "View driver performance",
    trip_history_sub: "Trip history",
    view_earnings: "View earnings",
    live_locations: "Live vehicle locations",
    assign_to_vehicles: "Assign drivers to vehicles",
    end_session: "End your session",
    about: "About",
    about_sub: "Terms, privacy & more",
    settings_label: "Settings",

    // About page
    terms_conditions: "Terms and Conditions",
    terms_sub: "Read our terms",
    privacy_policy: "Privacy Policy",
    privacy_sub: "How we handle your data",

    // Delete account
    request_received: "Request Received",
    back_to_login: "Back to Login",
    request_account_deletion: "Request Account Deletion",

    // Fleet management
    idle_drivers: "Idle Drivers",
    assigned: "Assigned",
    unassigned: "Unassigned",
    idle: "Idle",
    change_driver: "Change driver",
    unassign: "Unassign",
    assign_driver: "Assign Driver",
    no_driver_assigned_sub: "No driver assigned to this vehicle",
    no_vehicles_found: "No vehicles found",
    all_drivers_assigned: "All drivers are assigned to vehicles.",
    search_reg_or_driver: "Search by registration or driver name",
    fleet_management_sub: "Assign drivers to your vehicles",

    // Common dialog
    no_details: "No details available",
  },
  ta: {
    // Navigation
    home: "முகப்பு",
    trips: "பயணங்கள்",
    earnings: "வருமானம்",
    profile: "சுயவிவரம்",
    report: "அறிக்கை",
    vehicles: "வாகனங்கள்",
    drivers: "ஓட்டுநர்கள்",
    dashboard: "டாஷ்போர்டு",

    // Profile menu
    my_profile: "என் சுயவிவரம்",
    notifications: "அறிவிப்புகள்",
    theme: "தீம்",
    language: "மொழி",
    settings: "அமைப்புகள்",
    sign_out: "வெளியேறு",
    dark_mode: "இருண்ட பயன்முறை",
    light_mode: "ஒளி பயன்முறை",
    manage_alerts: "உங்கள் எச்சரிக்கைகளை நிர்வகிக்கவும்",
    app_preferences: "செயலி விருப்பங்கள் & ஆதரவு",
    log_out: "உங்கள் கணக்கிலிருந்து வெளியேறு",
    english: "ஆங்கிலம்",
    tamil: "தமிழ்",
    select_language: "உங்கள் மொழியை தேர்ந்தெடுக்கவும்",

    // Driver info
    driver_information: "ஓட்டுநர் தகவல்",
    driver_app: "ஓட்டுநர் செயலி",
    phone: "தொலைபேசி",
    driver_id: "ஓட்டுநர் ID",
    vehicle_reg: "வாகன பதிவு எண்",
    vehicle_id: "வாகன ID",
    online: "இணையில்",
    offline: "இணையற்று",

    // Trip status
    head_to_pickup: "பிக்கப் நோக்கி செல்லவும்",
    customer_waiting: "வாடிக்கையாளர் காத்திருக்கிறார் — பயணம் தொடங்கவும்",
    trip_in_progress: "பயணம் நடக்கிறது",

    // Actions
    accept: "ஏற்றுக்கொள்",
    decline: "மறு",
    reached: "சென்றடைந்தேன்",
    start_trip: "பயணம் தொடங்கு",
    end_trip: "பயணம் முடி",
    cancel_trip: "பயணம் ரத்து",
    cancel: "ரத்து",
    confirm: "உறுதிப்படுத்து",
    submit: "சமர்ப்பி",
    skip: "தவிர்",
    call: "அழை",

    // Trip page
    trip_history: "பயண வரலாறு",
    all: "அனைத்தும்",
    completed: "முடிந்தது",
    cancelled: "ரத்தானது",
    no_trips: "பயணங்கள் இல்லை",
    complete_first_trip: "உங்கள் முதல் பயணத்தை முடிக்கவும்",
    loading_trips: "பயணங்கள் ஏற்றுகிறது…",

    // Earnings page
    net_earnings: "நிகர வருமானம்",
    total_collection: "மொத்த வசூல்",
    today: "இன்று",
    week: "வாரம்",
    month: "மாதம்",
    this_week: "இந்த வாரம்",
    yesterday: "நேற்று",
    last_7_days: "கடந்த 7 நாட்கள்",
    custom_range: "குறிப்பிட்ட காலம்",
    penalties: "அபராதங்கள்",
    gross_earnings: "மொத்த வருமானம்",
    net: "நிகர",
    commission: "கமிஷன்",
    transaction_history: "பரிவர்த்தனை வரலாறு",
    no_transactions: "பரிவர்த்தனைகள் இல்லை",
    loading_earnings: "வருமானம் ஏற்றுகிறது…",
    trip_earning: "பயணம்",
    bonus: "போனஸ்",
    penalty: "அபராதம்",
    deduction: "கழிவு",

    // Performance page
    performance_report: "செயல்திறன் அறிக்கை",
    performance: "செயல்திறன்",
    ratings: "மதிப்பீடுகள்",
    total: "மொத்தம்",
    total_bookings: "மொத்த முன்பதிவுகள்",
    completed_trips: "முடிந்த பயணங்கள்",
    cancelled_trips: "ரத்தான பயணங்கள்",
    worked_hours: "பணி நேரம்",
    total_hours: "மொத்த நேரம்",
    online_hours: "ஆன்லைன் நேரம்",
    on_trip_hours: "பயண நேரம்",
    commission_rate: "கமிஷன் விகிதம்",
    rating_breakdown: "மதிப்பீடு விவரம்",
    excellent: "சிறந்தது",
    good: "நல்லது",
    average: "சராசரி",
    below_avg: "சராசரிக்கு கீழ்",
    poor: "மோசம்",
    based_on: "அடிப்படையில்",
    ratings_label: "மதிப்பீடுகள்",
    loading_report: "அறிக்கை ஏற்றுகிறது…",

    // Notifications
    no_notifications: "இன்னும் அறிவிப்புகள் இல்லை",
    notifications_empty_sub: "பயண எச்சரிக்கைகள் இங்கே தெரியும்",
    loading_notifications: "அறிவிப்புகள் ஏற்றுகிறது…",

    // Trip completion
    trip_completed: "பயணம் முடிந்தது!",
    final_fare: "இறுதி கட்டணம்",
    cash_collect: "பணம் • வாடிக்கையாளரிடம் பெறவும்",
    received_cash: "பணம் பெற்றேன்",
    rate_customer: "வாடிக்கையாளரை மதிப்பிடுக",
    submit_rating: "மதிப்பீடு சமர்ப்பி",
    distance: "தூரம்",
    duration: "கால அளவு",
    payment: "பணம்",

    // OTP & Opening KM
    enter_otp: "OTP உள்ளிடவும்",
    enter_otp_sub: "வாடிக்கையாளரிடம் 4 இலக்க OTP கேளுங்கள்",
    verify_otp: "சரிபார்த்து பயணம் தொடங்கு",
    enter_opening_km: "தொடக்க கிலோமீட்டர் உள்ளிடவும்",
    opening_km_sub: "தொடங்குமுன் ஓடோமீட்டர் வாசிப்பை பதிவு செய்யவும்",
    odometer_reading: "ஓடோமீட்டர் வாசிப்பு (கி.மீ)",
    save_continue: "சேமி & தொடர்",

    // Cancel trip
    cancel_reason: "ரத்து காரணம்",
    select_reason: "காரணம் தேர்ந்தெடு",
    cancel_confirm: "நீங்கள் ரத்து செய்ய விரும்புகிறீர்களா?",

    // Availability
    go_online: "இணையில் செல்",
    go_offline: "இணையை விட்டு வெளியேறு",
    today_trips: "இன்றைய பயணங்கள்",
    today_earnings: "இன்றைய வருமானம்",

    // Vendor profile
    basic_information: "அடிப்படை தகவல்",
    document_verification: "ஆவண சரிபார்ப்பு",
    bank_details: "வங்கி விவரங்கள்",
    contact_person: "தொடர்பு நபர்",
    payment_mode: "பணம் செலுத்தும் முறை",
    address: "முகவரி",
    email: "மின்னஞ்சல்",
    additional_phone: "கூடுதல் தொலைபேசி",
    add_email: "மின்னஞ்சல் சேர்",
    date_of_birth: "பிறந்த தேதி",
    vendor_id_label: "வ.உ. எண்",
    verified: "சரிபார்க்கப்பட்டது",
    pending: "நிலுவையில்",
    aadhaar: "ஆதார்",
    pan: "PAN",
    gst: "GST",
    vendor_app: "விற்பனையாளர் செயலி",

    // Vendor dashboard
    welcome: "வரவேற்கிறோம்",
    active_vehicles: "செயலில் உள்ள வாகனங்கள்",
    active_drivers: "செயலில் உள்ள ஓட்டுநர்கள்",
    todays_trips: "இன்றைய பயணங்கள்",
    todays_revenue: "இன்றைய வருவாய்",
    alerts: "எச்சரிக்கைகள்",

    // Vendor trips
    fleet_trips: "வாகன பயணங்கள்",
    no_trips_yet: "இன்னும் பயணங்கள் இல்லை",

    // Vendor earnings
    earnings_ledger: "வருமானம் & பேரேடு",
    credits: "வரவுகள்",
    debits: "செலவுகள்",
    balance: "இருப்பு",
    recent_transactions: "சமீபத்திய பரிவர்த்தனைகள்",
    no_transactions_yet: "இன்னும் பரிவர்த்தனைகள் இல்லை",

    // Vendor vehicles
    my_vehicles: "என் வாகனங்கள்",
    no_vehicles: "வாகனங்கள் ஒதுக்கப்படவில்லை",
    insurance: "காப்பீடு",
    fitness: "ஃபிட்னஸ்",
    permit: "அனுமதி",

    // Vendor drivers
    my_drivers: "என் ஓட்டுநர்கள்",
    no_drivers: "ஓட்டுநர்கள் ஒதுக்கப்படவில்லை",

    // Vendor notifications
    updates_here: "புதுப்பிப்புகள் மற்றும் எச்சரிக்கைகள் இங்கே தெரியும்",

    // Common
    loading: "ஏற்றுகிறது…",
    no_data: "தரவு இல்லை",
    safe_track: "ஸ்விஃப்ட் டாக்சி",
    driver_app_version: "ஓட்டுநர் செயலி v1.0.0",
    transaction: "பரிவர்த்தனை",
    transactions: "பரிவர்த்தனைகள்",
    hrs: "மணி",
    km: "கி.மீ",
    min: "நிமிடம்",
    cash: "பணம்",
    vehicle_breakdown: "வாகன கோளாறு",
    personal_emergency: "தனிப்பட்ட அவசரநிலை",
    customer_unreachable: "வாடிக்கையாளரை அடைய முடியவில்லை",
    other: "மற்றவை",
    not_logged_in: "நீங்கள் உள்நுழையவில்லை",

    // Vendor dashboard extras
    welcome_back: "மீண்டும் வரவேற்கிறோம்,",
    fleet_today_sub: "உங்கள் வாகனங்களின் இன்றைய செயல்பாடுகள் இங்கே.",
    quick_actions: "விரைவு செயல்கள்",
    fleet_management: "வாகன மேலாண்மை",
    fleet_tracking: "வாகன கண்காணிப்பு",
    add_vehicle: "வாகனம் சேர்க்க",
    add_driver: "ஓட்டுநரை சேர்க்க",
    doc_expiry_overview: "ஆவண காலாவதி பார்வை",
    vehicle_performance: "வாகன செயல்திறன்",
    driver_performance: "ஓட்டுநர் செயல்திறன்",
    couldnt_load_dashboard: "டாஷ்போர்டு புள்ளிவிவரங்களை ஏற்ற முடியவில்லை. மீண்டும் முயற்சிக்க தட்டவும்.",
    no_vehicles_yet: "இன்னும் வாகனங்கள் இல்லை.",
    no_drivers_yet: "இன்னும் ஓட்டுநர்கள் இல்லை.",

    // Exit dialog
    exit_app: "செயலியிலிருந்து வெளியேறு",
    exit_app_confirm: "வெளியேற விரும்புகிறீர்களா?",
    yes: "ஆம்",
    no: "இல்லை",

    // Notifications page
    mark_all_read: "அனைத்தையும் படித்ததாக குறி",
    read: "படிக்கப்பட்டது",
    unread: "படிக்கப்படவில்லை",

    // Settings
    push_notifications: "புஷ் அறிவிப்புகள்",
    manage_alert_prefs: "எச்சரிக்கை விருப்பங்களை நிர்வகி",
    delete_account: "கணக்கை நீக்கு",
    permanently_remove: "உங்கள் கணக்கை நிரந்தரமாக நீக்கு",
    settings_sub: "அறிவிப்புகள், தீம், மொழி",

    // Document status
    valid: "செல்லுபடியாகும்",
    expiring: "காலாவதியாகிறது",
    expired: "காலாவதியானது",
    missing: "இல்லை",
    ins: "காப்பீடு",
    fit: "ஃபிட்னஸ்",
    pmt: "அனுமதி",

    // Period filters
    this_month: "இந்த மாதம்",
    no_trips_period: "இந்த காலகட்டத்தில் பயணங்கள் இல்லை",
    balance_label: "இருப்பு",

    // Tabs / status
    active_tab: "செயலில்",
    inactive_tab: "செயலற்ற",
    unverified: "சரிபார்க்கப்படாதது",
    no_active_vehicles: "செயலில் உள்ள வாகனங்கள் இல்லை",
    no_inactive_vehicles: "செயலற்ற வாகனங்கள் இல்லை",
    no_unverified_vehicles: "சரிபார்க்கப்படாத வாகனங்கள் இல்லை",
    no_active_drivers: "செயலில் உள்ள ஓட்டுநர்கள் இல்லை",
    no_inactive_drivers: "செயலற்ற ஓட்டுநர்கள் இல்லை",
    no_unverified_drivers: "சரிபார்க்கப்படாத ஓட்டுநர்கள் இல்லை",
    docs_required: "ஆவணங்கள் தேவை",
    suspended: "தடைசெய்யப்பட்டது",
    under_review: "மறுபரிசீலனையில்",
    add: "சேர்",

    // Profile menu subtitles
    manage_vehicles: "வாகனங்களை நிர்வகி",
    manage_drivers: "ஓட்டுநர்களை நிர்வகி",
    view_vehicle_perf: "வாகன செயல்திறனை பார்",
    view_driver_perf: "ஓட்டுநர் செயல்திறனை பார்",
    trip_history_sub: "பயண வரலாறு",
    view_earnings: "வருமானம் பார்",
    live_locations: "நேரடி வாகன இடங்கள்",
    assign_to_vehicles: "ஓட்டுநர்களை வாகனங்களுக்கு ஒதுக்கு",
    end_session: "உங்கள் அமர்வை முடிக்கவும்",
    about: "செயலியைப் பற்றி",
    about_sub: "விதிமுறைகள், தனியுரிமை & மேலும்",
    settings_label: "அமைப்புகள்",

    // About page
    terms_conditions: "விதிமுறைகள் & நிபந்தனைகள்",
    terms_sub: "எங்கள் விதிமுறைகளை படிக்கவும்",
    privacy_policy: "தனியுரிமை கொள்கை",
    privacy_sub: "உங்கள் தரவை எவ்வாறு கையாள்கிறோம்",

    // Delete account
    request_received: "கோரிக்கை பெறப்பட்டது",
    back_to_login: "உள்நுழைவுக்கு திரும்பு",
    request_account_deletion: "கணக்கு நீக்க கோரிக்கை",

    // Fleet management
    idle_drivers: "ஓய்வில் உள்ள ஓட்டுநர்கள்",
    assigned: "ஒதுக்கப்பட்டது",
    unassigned: "ஒதுக்கப்படவில்லை",
    idle: "ஓய்வு",
    change_driver: "ஓட்டுநரை மாற்று",
    unassign: "ஒதுக்கீட்டை நீக்கு",
    assign_driver: "ஓட்டுநரை ஒதுக்கு",
    no_driver_assigned_sub: "இந்த வாகனத்திற்கு ஓட்டுநர் ஒதுக்கப்படவில்லை",
    no_vehicles_found: "வாகனங்கள் கண்டறியப்படவில்லை",
    all_drivers_assigned: "அனைத்து ஓட்டுநர்களும் வாகனங்களுக்கு ஒதுக்கப்பட்டுள்ளனர்.",
    search_reg_or_driver: "பதிவு எண் அல்லது ஓட்டுநர் பெயரால் தேடவும்",
    fleet_management_sub: "உங்கள் வாகனங்களுக்கு ஓட்டுநர்களை ஒதுக்கவும்",

    // Common dialog
    no_details: "விவரங்கள் இல்லை",
  },
} as const;

type TranslationKey = keyof typeof translations.en;

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  toggleLanguage: () => void;
  t: (key: TranslationKey) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

const STORAGE_KEY = "app-language";

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [language, setLanguageState] = useState<Language>(() => {
    if (typeof window !== "undefined") {
      return (localStorage.getItem(STORAGE_KEY) as Language) || "en";
    }
    return "en";
  });

  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
    localStorage.setItem(STORAGE_KEY, lang);
  };

  useEffect(() => {
    if (typeof document !== "undefined") {
      document.documentElement.lang = language;
    }
  }, [language]);

  const toggleLanguage = () => {
    setLanguage(language === "en" ? "ta" : "en");
  };

  const t = (key: TranslationKey): string => {
    return translations[language][key] ?? translations.en[key] ?? key;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, toggleLanguage, t }}>{children}</LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
}
